import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export const FeedbackForm = () => {
  const { t } = useLanguage();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const SUBJECT_MAX = 200;
  const MESSAGE_MAX = 2000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      toast({
        title: t('feedback.missingInfo'),
        description: t('feedback.fillBoth'),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase.functions.invoke('send-feedback', {
        body: { 
          subject: subject.trim(),
          message: message.trim(),
          userEmail: user?.email || 'anonymous'
        }
      });

      if (error) throw error;

      toast({
        title: t('feedback.sent'),
        description: t('feedback.thankYou')
      });

      setSubject("");
      setMessage("");
    } catch (error: any) {
      console.error('Error sending feedback:', error);
      
      let errorMessage = t('feedback.tryAgain');
      if (error.message?.includes("Subject")) {
        errorMessage = t('feedback.subjectLength');
      } else if (error.message?.includes("Message")) {
        errorMessage = t('feedback.messageLength');
      } else if (error.message?.includes("email")) {
        errorMessage = t('feedback.invalidEmail');
      }
      
      toast({
        title: t('feedback.error'),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="subject">{t('feedback.subject')}</Label>
        <Input
          id="subject"
          placeholder={t('feedback.subjectPlaceholder')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={loading}
          maxLength={SUBJECT_MAX}
        />
        <p className="text-xs text-muted-foreground text-right">
          {subject.length}/{SUBJECT_MAX} {t('feedback.characters')}
        </p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="message">{t('feedback.message')}</Label>
        <Textarea
          id="message"
          placeholder={t('feedback.messagePlaceholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={loading}
          rows={6}
          className="resize-none"
          maxLength={MESSAGE_MAX}
        />
        <p className="text-xs text-muted-foreground text-right">
          {message.length}/{MESSAGE_MAX} {t('feedback.characters')}
        </p>
      </div>

      <Button type="submit" disabled={loading} className="w-full">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('feedback.sendFeedback')}
      </Button>
    </form>
  );
};
